import { useState } from "react";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";

const STYLES = ["moderno e colorato", "elegante e minimale", "festoso con coriandoli", "natalizio", "estivo e luminoso", "retrò anni '80", "fotografico realistico"];
const PHASE_LABELS = { idle: "Attesa", playing: "Gioco", win: "Vincita", lose: "Perdita" };
const FORMAT_LABELS = { landscape: "orizzontale 16:9", portrait: "verticale 9:16", square: "quadrato 1:1" };

export function ScreenAiDialog({ contestId, phase, format, ratio, onGenerated }) {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState(STYLES[0]);
  const [prompt, setPrompt] = useState("");
  const [animate, setAnimate] = useState(phase === "idle");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const doc = await api.aiScreen(contestId, { phase, format, style, prompt, animate, ratio });
      onGenerated(doc);
      toast.success(animate ? "GIF animata generata e inserita nella cella" : "Immagine generata e inserita nella cella");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generazione non riuscita");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={`screen-ai-${phase}-${format}`}
        className="h-7 w-full rounded-md border border-violet-500/40 text-violet-300 hover:bg-violet-500/10 grid place-items-center text-[11px]">
        <span className="flex items-center gap-1"><Sparkles className="h-3 w-3" /> Genera con AI</span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="screen-ai-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-violet-400" /> Videata {PHASE_LABELS[phase]} · {FORMAT_LABELS[format] || `formato personalizzato (${ratio > 1 ? "orizzontale" : "verticale"})`}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">L'AI crea lo sfondo nel formato esatto della cella usando nome del concorso, premi e fase. Ricordati di salvare la grafica dopo.</p>
          <div className="space-y-1.5">
            <Label className="text-xs">Stile</Label>
            <select value={style} onChange={(e) => setStyle(e.target.value)} className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="screen-ai-style">
              {STYLES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Indicazioni aggiuntive (facoltative)</Label>
            <Input value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Es. carrello della spesa, colori del brand rosso e bianco…" data-testid="screen-ai-prompt" />
          </div>
          <label className="flex items-start gap-2 text-xs cursor-pointer rounded-lg bg-secondary/40 p-3">
            <input type="checkbox" checked={animate} onChange={(e) => setAnimate(e.target.checked)} className="h-4 w-4 accent-violet-500 mt-0.5" data-testid="screen-ai-animate" />
            <span><b>GIF animata</b> (zoom lento in loop) <span className="block text-muted-foreground">Consigliata per la videata di attesa. Se disattivata viene creata un'immagine statica JPEG.</span></span>
          </label>
          <Button onClick={run} disabled={busy} className="w-full gap-2 bg-violet-600 hover:bg-violet-500" data-testid="screen-ai-run">
            <Sparkles className="h-4 w-4" /> {busy ? "Generazione in corso (30-60 s)…" : "Genera e inserisci"}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
